import type { BulkDeleteFormRequest } from 'epic/batchWorkspace/deleteBatchFormsEpic';
import type { IdType } from 'epic/common-form/ReloadFormRequest';
import type { BatchImportMetadataRequest, BatchImportMetadataResponse } from 'models/batch/import-metadata';
import { BatchEnrichFormRequest } from 'models/forms/BatchEnrichFormRequest';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { ProcessingChain } from 'models/ProcessingChain';
import type { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IHttpClient } from 'utils/http/httpClient.model';
import type { ImportFormRequest } from 'utils/import/import.models';

export interface InitFormRequest {
  productType: string;
  productSubType: string;
  wrapper: string;
  payoffType?: string;
  tradeDate: string;
  isPrivateMode?: boolean;
  currency?: string;
  selectedOnBehalfClient?: ISalesCounterpart;
  xMargin?: number;
}
export interface BatchFormResults {
  forms: BatchFormResult[];
}
export interface BulkCloneFormRequest {
  productType: string;
  productSubType: string;
  forms: CloneFormRequest[];
}
export interface CloneFormRequest {
  negotiationId: string;
}
export interface ReloadFormRequest {
  id: string;
  idType: IdType;
  tradeDate: string;
  productType: string;
  productSubType: string;
  processingChain?: ProcessingChain;
  pricerRfqId?: string;
  isPrivateMode?: boolean;
}
export interface RestoreFormRequest {
  negotiationId: string;
  pricerRfqId?: string;
  quoteId?: string;
}

export interface BatchImportFormApi {
  importForm(request: ImportFormRequest): Observable<BatchFormResult>;
}

export function batchWorkspaceApiConnector(http$: IHttpClient) {
  const batchWorkspaceApi = 'api/v1/batch-form';
  return {
    initForm(request: InitFormRequest) {
      return http$
        .postJSON<BatchFormResult>({ url: batchWorkspaceApi, body: request })
        .pipe(map(({ payload }) => payload));
    },
    enrichForm({ negotiationId, version, ...request }: BatchEnrichFormRequest) {
      return http$
        .patch<BatchFormResult>({ url: `${batchWorkspaceApi}/${negotiationId}/version/${version}`, body: request })
        .pipe(map(({ payload }) => payload));
    },
    bulkDeleteForms(request: BulkDeleteFormRequest) {
      return http$
        .postJSON<typeof EMPTY>({ url: `${batchWorkspaceApi}/bulk-delete`, body: request })
        .pipe(map(r => r.payload));
    },
    bulkCloneForms(request: BulkCloneFormRequest) {
      return http$
        .postJSON<BatchFormResults>({ url: `${batchWorkspaceApi}/bulk-clone`, body: request })
        .pipe(map(r => r.payload));
    },
    reloadForm(request: ReloadFormRequest) {
      return http$
        .postJSON<BatchFormResult>({ url: `${batchWorkspaceApi}/reload`, body: request })
        .pipe(map(({ payload }) => payload));
    },
    restoreForm(request: RestoreFormRequest) {
      return http$
        .postJSON<BatchFormResult>({ url: `${batchWorkspaceApi}/restore`, body: request })
        .pipe(map(({ payload }) => payload));
    },
    getImportMetadata(request: BatchImportMetadataRequest) {
      return http$
        .postJSON<BatchImportMetadataResponse>({ url: `${batchWorkspaceApi}/import-metadata`, body: request })
        .pipe(map(({ payload }) => payload));
    },
    importForm(request: ImportFormRequest) {
      return http$
        .postJSON<BatchFormResult>({ url: `${batchWorkspaceApi}/import`, body: request })
        .pipe(map(({ payload }) => payload));
    },
  };
}
